/**
 * Policies list — the user's last layout (OR-1664).
 *
 * Reads the saved blob once on mount and writes it back on every commit.
 * The backend PUT is a **full replacement** of the blob, which drives two
 * rules carried over from Triage's saved views (OR-1384):
 *
 *  1. **Never write when the read failed.** A failed GET must not degrade
 *     into an empty layout that then overwrites whatever the server holds.
 *     The page keeps Filters / Customize disabled for the session instead
 *     of dropping edits without saying so.
 *  2. **One write at a time.** Two PUTs in flight can land out of order
 *     and leave the server holding the older layout, so writes are
 *     chained.
 *
 * Reads otherwise fail quietly into "no saved layout": preferences are a
 * convenience and must never take the page down.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { VIEW_KEYS, getViewPreference, putViewPreference } from "../../api";
import { type PolicyLayout, parseLayout, serializeLayout } from "./view-preference-config";

export interface PoliciesLayoutState {
    /** `loading` until the initial fetch settles — success or failure. */
    status: "loading" | "ready";
    /** What the user last left the page in; null when nothing was saved. */
    layout: PolicyLayout | null;
    /** False when the initial read failed (rule 1). */
    canWrite: boolean;
    /** Record the layout the page is now in. Fire-and-forget. */
    remember: (layout: PolicyLayout) => void;
}

export function usePoliciesLayout(): PoliciesLayoutState {
    const [status, setStatus] = useState<"loading" | "ready">("loading");
    const [layout, setLayout] = useState<PolicyLayout | null>(null);
    const [canWrite, setCanWrite] = useState(false);
    const readable = useRef(false);
    /** The write chain (rule 2). */
    const queue = useRef<Promise<unknown>>(Promise.resolve());
    /** The latest layout asked for, read at send time so a queued write
     *  carries the newest value rather than a stale snapshot. */
    const latest = useRef<PolicyLayout | null>(null);

    useEffect(() => {
        let cancelled = false;
        getViewPreference(VIEW_KEYS.policies)
            .then((config) => {
                if (cancelled) return;
                readable.current = true;
                const parsed = parseLayout(config);
                latest.current = parsed;
                setLayout(parsed);
                setCanWrite(true);
            })
            .catch(() => {
                // Rule 1: stay read-only for the session.
            })
            .finally(() => {
                if (!cancelled) setStatus("ready");
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const remember = useCallback((next: PolicyLayout) => {
        if (!readable.current) return;
        latest.current = next;
        const task = async () => {
            const sent = latest.current;
            if (!sent) return;
            try {
                await putViewPreference(VIEW_KEYS.policies, serializeLayout(sent));
            } catch {
                // Dropped silently: the next commit re-sends the layout.
            }
        };
        queue.current = queue.current.then(task, task);
    }, []);

    return { status, layout, canWrite, remember };
}
